import { getCore } from '../../../client/core/getCore';
import { getDefaultClient } from '../../../client/defaultClient';
import { createApiClient } from '../../apiClient';
import { consumeCaptchaToken } from '../../captcha/consumeCaptchaToken';
import type { OTPVerification } from '../otp.types';
import type { CountryCode } from './supportedCountries';
import { supportedCountries } from './supportedCountries';

export type SendSmsOTPParams = {
  isoCountryCode: CountryCode;
  phoneNumber: string;
};

/**
 * Sends a one-time password (OTP) via SMS to the specified phone number.
 *
 * @param params.isoCountryCode - The ISO country code for the phone number (e.g., 'US', 'CA', 'GB').
 * @param params.phoneNumber - The phone number to send the OTP to.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to an OTPVerification object containing the phone details and verification UUID.
 */
export const sendSmsOTP = async (
  { isoCountryCode, phoneNumber }: SendSmsOTPParams,
  client = getDefaultClient()
): Promise<OTPVerification> => {
  const core = getCore(client);
  const apiClient = createApiClient({}, client);

  const phoneCountryCode = supportedCountries[isoCountryCode].code;

  const { verificationUUID } = await apiClient.createSmsVerification({
    environmentId: core.environmentId,
    smsVerificationCreateRequest: {
      captchaToken: consumeCaptchaToken(client),
      isoCountryCode,
      phoneCountryCode,
      phoneNumber,
    },
  });

  return { isoCountryCode, phoneCountryCode, phoneNumber, verificationUUID };
};
